
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ExternalLink, Loader2 } from 'lucide-react';
import { ArxivRecord } from './types';
import { formatDate } from './utils';
import { useArxivImport } from './hooks/useArxivImport';

interface ArxivRecordCardProps {
  record: ArxivRecord;
  index: number;
}

const ArxivRecordCard: React.FC<ArxivRecordCardProps> = ({ record, index }) => {
  const { importPaper, isImporting } = useArxivImport();

  const handleImport = async () => {
    await importPaper(record);
  };

  return (
    <Card key={`${record.id}-${index}`} className="border-l-4 border-l-blue-500">
      <CardContent className="pt-4">
        <div className="space-y-3">
          <div>
            <h3 className="font-semibold text-lg leading-tight">{record.title}</h3>
            <p className="text-sm text-muted-foreground">
              arXiv:{record.id} • Published: {formatDate(record.publishedDate)} • 
              Versions: {record.versions}
            </p>
          </div>
          
          <div>
            <p className="text-sm font-medium text-muted-foreground mb-1">Authors:</p>
            <p className="text-sm">{record.authors.join(', ')}</p>
          </div>
          
          <div className="flex flex-wrap gap-1">
            {record.categories.map((category, catIndex) => (
              <Badge key={catIndex} variant="secondary" className="text-xs">
                {category}
              </Badge>
            ))}
          </div>
          
          <div>
            <p className="text-sm font-medium text-muted-foreground mb-1">Abstract:</p>
            <p className="text-sm text-muted-foreground line-clamp-3">
              {record.abstract}
            </p>
          </div>
          
          {record.license && (
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-1">License:</p>
              <Badge variant="outline" className="text-xs">
                {record.license}
              </Badge>
            </div>
          )}
          
          <div className="flex gap-2 flex-wrap">
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => window.open(record.pdfUrl, '_blank')}
              className="flex items-center gap-1"
            >
              <ExternalLink className="h-3 w-3" />
              PDF
            </Button>
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => window.open(`https://arxiv.org/abs/${record.id}`, '_blank')}
              className="flex items-center gap-1"
            >
              <ExternalLink className="h-3 w-3" />
              arXiv Page
            </Button>
            {record.doi && (
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => window.open(`https://doi.org/${record.doi}`, '_blank')}
                className="flex items-center gap-1"
              >
                <ExternalLink className="h-3 w-3" />
                DOI
              </Button>
            )}
            <Button 
              size="sm" 
              variant="default"
              onClick={handleImport}
              disabled={isImporting}
              className="flex items-center gap-1 ml-auto"
            >
              {isImporting ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              )}
              {isImporting ? 'Importing...' : 'Import to Database'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ArxivRecordCard;
